'use client';

import { useState } from 'react';
import { useTranslations } from 'use-intl';
import Drawer from '@/components/organisms/drawer';
import Button from '@/components/atoms/button';

// EWM-299 (OQ-5) — list name max length, mirrored by the BFF
// (validateWishlistName). Keep the two in sync.
const NAME_MAX_LENGTH = 50;

interface CreateListDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (name: string, description: string) => Promise<boolean>;
  // Names of the customer's existing lists, used for the inline
  // duplicate-name check (trim + case-insensitive). The BFF enforces the
  // same rule at the boundary.
  existingNames?: string[];
}

const inputClass =
  'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-14 text-primary placeholder:text-gray-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20';
const labelClass = 'text-13 mb-2 block text-left font-semibold text-primary';

const CreateListDrawer = ({ isOpen, onClose, onSubmit, existingNames = [] }: CreateListDrawerProps) => {
  const translate = useTranslations('dashboard');
  const translateCommon = useTranslations('common');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const trimmedName = name.trim();
  const isDuplicate =
    trimmedName.length > 0 &&
    existingNames.some((existing) => existing.trim().toLowerCase() === trimmedName.toLowerCase());
  const canSubmit = trimmedName.length > 0 && !isDuplicate;

  const handleClose = () => {
    setName('');
    setDescription('');
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    const ok = await onSubmit(trimmedName, description.trim());
    setSubmitting(false);
    if (ok) handleClose();
  };

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} headline={translate('purchase-list-create-new')} direction="right">
      <form onSubmit={handleSubmit} noValidate className="flex min-h-full flex-col">
        <div className="flex-1 px-6 py-6">
          <p className="text-13 mb-6 text-gray-500">{translate('purchase-list-create-description')}</p>

          <div className="flex flex-col gap-5">
            <div>
              <label htmlFor="create-list-name" className={labelClass}>
                {translateCommon('name')} *
              </label>
              <input
                id="create-list-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={translate('purchase-list-name-placeholder')}
                required
                maxLength={NAME_MAX_LENGTH}
                aria-invalid={isDuplicate}
                aria-describedby={isDuplicate ? 'create-list-name-error' : undefined}
                className={inputClass}
              />
              {isDuplicate && (
                <p id="create-list-name-error" className="text-12 mt-1 text-red-600">
                  {translate('purchase-list-name-duplicate')}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="create-list-desc" className={labelClass}>
                {translateCommon('description')}
              </label>
              <textarea
                id="create-list-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={translate('purchase-list-description-placeholder')}
                rows={4}
                className={`${inputClass} resize-none`}
              />
              <p className="text-12 mt-1 text-gray-400">{translate('purchase-list-description-hint')}</p>
            </div>
          </div>
        </div>

        <div className="sticky bottom-0 border-t border-gray-200 bg-gray-50 px-6 py-4">
          <Button
            type="submit"
            variant="primary"
            size="m"
            className="w-full"
            loading={submitting}
            disabled={!canSubmit}
          >
            {translate('purchase-list-create-new')}
          </Button>
        </div>
      </form>
    </Drawer>
  );
};

export default CreateListDrawer;
